import type {
	TCvCode,
	TCvExperience,
	TFile,
	TPartialCvExperienceWithId,
	TTeacherProfile,
	TPendingData,
	TPendingExperience,
	TNotification,
} from "@/assets/js/globalTypes";
import ProfileImage from "@/assets/image/dai-dai-placeholder-profile.png";
import {
	compareArrays,
	deepEqual,
	Errors,
	setupChangesFields,
} from "@/assets/js/helpers";
import { DateInstance } from "@/assets/js/dateHelper";

export const useProfileStore = defineStore("profile", () => {
	const profile = ref<TTeacherProfile>();
	function updateProfile(data: TTeacherProfile) {
		handleExperienceSetup(data);
		profile.value = data;
		setupChanges();
	}

	const profilePending = ref<TPendingData>();

	const newImage = ref<File>();

	const profileChanges = ref<Partial<TTeacherProfile>>({});
	const profileChangesCopy = ref<Partial<TTeacherProfile>>({});

	const hasNewUnreadNotification = ref<boolean>(false); // ws
	const notifications = ref<TNotification[]>([]);

	const finishedProfileSetup = computed(
		() => profile.value && !!profile.value.usr_profilecompleted,
	);

	const accountApproved = computed(
		() => profile.value && !!profile.value.usr_accountapproved,
	);

	const errors = ref(new Errors());

	function checkHasChanges() {
		if (!profileChanges.value || !Object.keys(profileChanges.value).length) {
			return false;
		}

		for (const key in Object.keys(profile.value)) {
			if (Array.isArray(profile.value[key])) {
				if (!compareArrays(profile.value[key], profileChanges.value[key])) {
					return true;
				}
			} else if (profile.value[key] !== profileChanges.value[key]) {
				return true;
			}
		}
		return false;
	}

	const languageChangesCodes = computed({
		get: () => {
			if (!profileChanges.value || !profileChanges.value.languages) return [];

			return profileChanges.value.languages.map((lang) => lang.lng_code);
		},
		set: () => {},
	});

	const cityChangesIds = computed({
		get: () => {
			if (!profileChanges.value || !profileChanges.value.cities) return [];

			return profileChanges.value.cities.map((city) => city.cit_id);
		},
		set: () => {},
	});

	const contractChangesCodes = computed({
		get: () => {
			if (!profileChanges.value || !profileChanges.value.contract_types)
				return [];

			return profileChanges.value.contract_types.map(
				(contract) => contract.ctt_code,
			);
		},
		set: () => {},
	});

	const skillChangesIds = computed({
		get: () => {
			if (!profileChanges.value || !profileChanges.value.skills) return [];

			return profileChanges.value.skills.map((skill) => skill.tag_id);
		},
		set: () => {},
	});

	const usrImage = computed(() => {
		if (!profile.value || !profile.value.usr_avatar) {
			return ProfileImage;
		}

		if (profile.value.usr_avatar.startsWith("http")) {
			return profile.value.usr_avatar;
		}
		// return import.meta.env.MODE === "development"
		// ? `${import.meta.env.VITE_APP_ROOT_API}/${profile.value.usr_avatar}`
		return `${import.meta.env.VITE_APP_ROOT_API}/${profile.value.usr_avatar}`;
	});

	const usrImageChange = computed(() => {
		if (newImage.value) {
			return URL.createObjectURL(newImage.value);
		}
		if (!profileChanges.value) {
			return ProfileImage;
		}

		// if (import.meta.env.MODE === "development") {
		return usrImage.value;
		// }
		// return profileChanges.value.usr_avatar;
	});

	function cleanEmptyExperiences() {
		setupChangesFields(profileChanges.value, profileChangesCopy);

		const experiences = profileChangesCopy.value.experiences;
		const isEmptyEduc = (exp: any) =>
			exp.cve_title === "" &&
			exp.cve_start === null &&
			exp.cve_company === "" &&
			exp.cve_end === null &&
			exp.cvt_code === "EDUC" &&
			exp.ongoing === false;
		const isEmptyPexp = (exp: any) =>
			exp.cve_title === "" &&
			exp.cve_start === null &&
			exp.cve_company === "" &&
			exp.cve_end === null &&
			exp.cve_description === "" &&
			exp.cvt_code === "PEXP" &&
			exp.ongoing === false;
		const isEmptyTexp = (exp: any) =>
			exp.cve_title === "" &&
			exp.cve_start === null &&
			exp.cve_company === "" &&
			exp.cve_end === null &&
			exp.cve_description === "" &&
			exp.cvt_code === "TEXP" &&
			exp.cve_coursetitle === "" &&
			exp.cve_courselevel === null &&
			exp.cve_hourlyvolume === 0 &&
			// exp.cve_coursecontent === "" &&
			exp.ongoing === false;

		if (experiences.EDUC) {
			profileChangesCopy.value.experiences.EDUC = experiences.EDUC.filter(
				(exp: any) => !isEmptyEduc(exp),
			);
			if (profileChangesCopy.value.experiences.EDUC.length === 0)
				delete profileChangesCopy.value.experiences.EDUC;
		}
		if (experiences.PEXP) {
			profileChangesCopy.value.experiences.PEXP = experiences.PEXP.filter(
				(exp: any) => !isEmptyPexp(exp),
			);
			if (profileChangesCopy.value.experiences.PEXP.length === 0)
				delete profileChangesCopy.value.experiences.PEXP;
		}
		if (experiences.TEXP) {
			profileChangesCopy.value.experiences.TEXP = experiences.TEXP.filter(
				(exp: any) => !isEmptyTexp(exp),
			);
			if (profileChangesCopy.value.experiences.TEXP.length === 0)
				delete profileChangesCopy.value.experiences.TEXP;
		}
	}

	const hasChanges = computed(() => {
		cleanEmptyExperiences();
		if (usrImageChange.value !== usrImage.value) return true;
		return !deepEqual(profile.value, profileChangesCopy.value);
	});

	function setupChanges() {
		setupChangesFields(profile.value, profileChanges);
	}

	function setNewImage(file: File) {
		newImage.value = file;
	}

	function getChangedExperiences() {
		const res: {
			removed: TCvExperience[];
			new: TCvExperience[];
			edited: TPartialCvExperienceWithId[];
		} = {
			removed: [],
			new: [],
			edited: [],
		};
		if (!profileChanges.value || !profileChanges.value.experiences) {
			return res;
		}
		const experience_keys = [
			"cve_company",
			// "cve_coursecontent",
			"cve_courselevel",
			"cve_coursetitle",
			"cve_description",
			"cve_end",
			"cve_hourlyvolume",
			"cve_start",
			"cve_title",
		];
		for (const key of Object.keys(profileChanges.value.experiences)) {
			if (!profileChanges.value?.experiences[key]) {
				continue;
			}
			if (profile.value?.experiences?.[key]) {
				for (const experience of profile.value.experiences[key]) {
					const found = profileChanges.value.experiences[key].find(
						(item) => item.cve_id === experience.cve_id,
					);
					if (found) {
						const edits: TPartialCvExperienceWithId = {
							cve_id: found.cve_id,
						};
						let edited = false;
						for (const key of experience_keys) {
							if (found[key] !== experience[key]) {
								edited = true;
								edits[key] = found[key];
							}
						}
						if (edited) {
							edits.cvt_code = key as TCvCode;
							res.edited.push(edits);
						}
					} else {
						res.removed.push(experience);
					}
				}
			}
			for (const experience of profileChanges.value.experiences[key]) {
				if (experience.cve_id < 0) {
					res.new.push(experience);
					continue;
				}
			}
		}
		return res;
	}

	let cvItemIdCounter = -1;
	function addNewExperienceItem(type: TCvCode) {
		if (
			!profileChanges.value.experiences ||
			!profileChanges.value.experiences[type]
		) {
			profileChanges.value.experiences[type] = [];
		}
		if (type === "EDUC") {
			profileChanges.value.experiences["EDUC"].push({
				cve_id: cvItemIdCounter--,
				cve_title: "",
				cve_start: null,
				cve_company: "",
				cve_end: null,
				cvt_code: "EDUC",
				ongoing: false,
			});
		} else if (type === "PEXP") {
			profileChanges.value.experiences["PEXP"].push({
				cve_id: cvItemIdCounter--,
				cve_title: "",
				cve_start: null,
				cve_company: "",
				cve_end: null,
				cve_description: "",
				cvt_code: "PEXP",
				ongoing: false,
			});
		} else {
			profileChanges.value.experiences["TEXP"].push({
				cve_id: cvItemIdCounter--,
				cve_title: "",
				cve_start: null,
				cve_company: "",
				cve_end: null,
				cve_description: "",
				cvt_code: "TEXP",
				cve_coursetitle: "",
				cve_courselevel: null,
				cve_hourlyvolume: 0,
				// cve_coursecontent: "",
				ongoing: false,
			});
		}
	}

	function removeFile(file: TFile) {
		const removeFromArray = (arr: TFile[], file: TFile) => {
			for (let i = 0; i < arr.length; i++) {
				if (arr[i].fil_id === file.fil_id) {
					arr.splice(i, 1);
					break;
				}
			}
		};
		removeFromArray(profile.value.files, file);
		removeFromArray(profileChanges.value.files, file);
	}

	function handleExperienceSetup(data: TTeacherProfile) {
		if (!data.experiences) {
			// data.experiences = {};
			return;
		}
		const keys = Object.keys(data.experiences);
		for (const key of keys) {
			if (!data.experiences[key]) {
				continue;
			}
			for (const experience of data.experiences[key]) {
				// experience.ongoing = experience.cve_end  ? false : true;
				experience.ongoing =
					DateInstance.getFormattedDate(
						experience.cve_end,
						"YYYY-MM-DD",
						false,
					) === "3000-01-01"
						? true
						: false;
			}
		}
	}

	function removeFileByCode(file: TFile) {
		const removeFromArray = (arr: TFile[], file: TFile) => {
			for (let i = 0; i < arr.length; i++) {
				if (arr[i].ftp_code === file.ftp_code) {
					arr.splice(i, 1);
					break;
				}
			}
		};
		// removeFromArray(profile.value.files, file);
		removeFromArray(profileChanges.value.files, file);
	}

	function addFile(file: TFile) {
		if (!profileChanges.value || !profile.value) {
			return;
		}
		if (!profileChanges.value.files) {
			profileChanges.value.files = [];
		}
		if (!profile.value.files) {
			profile.value.files = [];
		}
		removeFileByCode(file);
		profileChanges.value.files.push(file);
		// profile.value.files.push(file);
	}

	function getEditedFields() {
		const editedFields: Omit<
			Partial<TTeacherProfile>,
			"experiences" | "usr_avatar" | "files"
		> &
			Partial<{
				experiences: {
					new: TCvExperience[];
					edited: TPartialCvExperienceWithId[];
					removed: TCvExperience[];
				};
				newImage: File;
				files: TFile[];
			}> = {};
		if (!profile.value || !profileChanges.value) {
			return editedFields;
		}
		// const keysToHandleManually: (keyof TTeacherProfile)[] = [
		// 	"experiences",
		// 	"files",
		// 	"subjects",
		// 	"languages",
		// 	"contract_types",
		// 	"skills",
		// 	"usr_avatar",
		// ];
		const keysToHandle: (keyof TTeacherProfile)[] = [
			"usr_fullname",
			"usr_email",
			"usr_phone",
			"usr_description",
			"usr_experience",
			"usr_professionalexperience",
			"usr_hourlyrate",
			"usr_remotework",
			"usr_title",
		];
		for (const key of keysToHandle) {
			const value = profileChanges.value[key];
			if (profile.value[key] !== value) {
				editedFields[key] = value;
			}
		}
		editedFields.experiences = getChangedExperiences();
		if (newImage.value) {
			editedFields.newImage = newImage.value;
		}
		// Add changed file IDs
		const changedFileIds: TFile[] = [];
		if (profile.value.files && profileChanges.value.files) {
			for (const changeFile of profileChanges.value.files) {
				const originalFile = profile.value.files.find(
					(f) => f.ftp_code === changeFile.ftp_code,
				);
				if (!originalFile || originalFile.fil_id !== changeFile.fil_id) {
					changedFileIds.push(changeFile);
				}
			}
		}

		if (changedFileIds.length > 0) {
			editedFields.files = changedFileIds;
		}
		editedFields.skills = profileChanges.value.skills;
		editedFields.languages = profileChanges.value.languages;
		editedFields.contract_types = profileChanges.value.contract_types;
		editedFields.cities = profileChanges.value.cities;
		editedFields.subjects = profileChanges.value.subjects;
		return editedFields;
	}

	function isFieldPending(
		group: "columns" | "files",
		key: string | number,
	): boolean {
		if (group === "columns") {
			const isPending = profilePending.value?.columns?.includes(
				key as TPendingData["columns"][number],
			);
			return isPending;
		}

		if (group === "files") {
			return profilePending.value.files.includes(key as number);
		}

		return false;
	}

	function isExperienceFieldPending(
		id: number,
		column: TPendingExperience["columns"][number],
	): boolean {
		if (!profilePending.value) return false;

		const experience = profilePending.value.experiences.find(
			(exp) => exp.cve_id === id,
		);

		if (!experience) return false;

		return experience.columns.includes(column);
	}

	function resetProfile() {
		profile.value = undefined;
		profileChanges.value = {};
		newImage.value = undefined;
		profilePending.value = undefined;
		profileChangesCopy.value = {};
	}

	return {
		profile,
		updateProfile,
		usrImage,
		profileChanges,
		setupChanges,
		usrImageChange,
		setNewImage,
		resetProfile,
		languageChangesCodes,
		cityChangesIds,
		contractChangesCodes,
		checkHasChanges,
		skillChangesIds,
		addNewExperienceItem,
		removeFile,
		addFile,
		finishedProfileSetup,
		newImage,
		errors,
		getEditedFields,
		getChangedExperiences,
		accountApproved,
		profilePending,
		isFieldPending,
		isExperienceFieldPending,
		hasNewUnreadNotification,
		notifications,
		hasChanges,
	};
});
